<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue'
import lx from '../../utils/lx'
import { scrollToSection } from '../../utils/scrollToSection'
import { CONTACT_EMAIL, FOOTER_PRODUCT_LINKS, FOOTER_SOCIALS, LX_BIDS } from './constants'

const activeSocial = ref<string | null>(null)

function onFooterClick(buttonName: string): void {
  lx.moduleClick(LX_BIDS.footerMc, { button_name: buttonName })
}

function onFooterNavClick(event: MouseEvent, buttonName: string, href: string): void {
  event.preventDefault()
  onFooterClick(buttonName)
  scrollToSection(href)
}

function onSocialActivate(label: string): void {
  onFooterClick(label)
  activeSocial.value = activeSocial.value === label ? null : label
}

function onDocPointer(event: Event): void {
  if (!activeSocial.value) return
  const target = event.target as Node | null
  const root = document.querySelector('.site-footer')
  if (root && target && !root.contains(target)) activeSocial.value = null
}

onMounted(() => {
  document.addEventListener('pointerdown', onDocPointer, { passive: true })
})

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocPointer)
})
</script>

<template>
  <footer class="site-footer">
    <!-- 顶部信封式波浪（设计稿白色顶边） -->
    <div class="footer-wave" aria-hidden="true">
      <svg viewBox="0 0 1440 110" preserveAspectRatio="none">
        <path d="M0,0 L1440,0 L1440,70 Q720,130 0,70 Z" fill="#FFFFFF" />
      </svg>
    </div>

    <div class="footer-body">
      <div class="footer-left">
        <img class="footer-logo" src="https://p0.meituan.net/mliveimage/01b2780454510b0f49fbd51ef100fec819691.png" alt="美团智播">

        <div class="footer-column">
          <a
            class="footer-column-title"
            href="#capSection"
            @click="onFooterNavClick($event, '产品能力', '#capSection')"
          >产品能力</a>
          <div class="footer-links footer-product-links">
            <a
              v-for="link in FOOTER_PRODUCT_LINKS"
              :key="link.href"
              class="footer-link"
              :href="link.href"
              @click="onFooterNavClick($event, link.label, link.href)"
            >{{ link.label }}</a>
          </div>
        </div>

        <div class="footer-column footer-contact">
          <div class="footer-column-title">联系我们</div>
          <div class="footer-links">
            <div class="footer-contact-row">
              <span class="label">地址</span>
              <span>北京市朝阳区望京数字创意园B座</span>
            </div>
            <div class="footer-contact-row">
              <span class="label">邮箱</span>
              <span class="footer-email">{{ CONTACT_EMAIL }}</span>
            </div>
            <div class="footer-contact-row footer-media-row">
              <span class="label">媒体</span>
              <div class="social-icons">
                <button
                  v-for="item in FOOTER_SOCIALS"
                  :key="item.label"
                  type="button"
                  class="social-item"
                  :class="{ 'is-active': activeSocial === item.label }"
                  :aria-label="`${item.label}二维码`"
                  :aria-expanded="activeSocial === item.label"
                  :data-mv-bid="LX_BIDS.footerMv"
                  :data-mv-key="`footer-social-${item.label}`"
                  :data-mv-button-name="item.label"
                  @mouseenter="onFooterClick(item.label)"
                  @click="onSocialActivate(item.label)"
                >
                  <img class="social-icon" :src="item.icon" :alt="item.label" width="23" height="24">
                  <span class="social-qr" role="tooltip">
                    <img :src="item.qr" :alt="`${item.label}二维码`" width="120" height="120">
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        class="footer-qr-wrap"
        :data-mv-bid="LX_BIDS.footerMv"
        data-mv-key="footer-xhs"
        data-mv-button-name="美团智播小红书"
        @click="onFooterClick('美团智播小红书')"
      >
        <img
          class="footer-qr"
          src="https://p0.meituan.net/mliveimage/daccc3830e05b7db5658e7df07fd3f5f29160.png"
          alt="美团智播小红书二维码"
          width="111"
          height="111"
        >
        <div class="footer-qr-label">小红书</div>
      </div>
    </div>

    <div class="footer-bottom">
      <div class="footer-divider" aria-hidden="true" />
      <div class="footer-copyright">
        <span>©2026 美团版权所有</span>
        <span class="beian">
          <a class="beian-icp" href="https://beian.miit.gov.cn/" target="_blank" rel="noopener noreferrer">京ICP备10211739-1号</a>
          <a href="http://www.beijing.gov.cn/zwgk/gongkai/gggs/" target="_blank" rel="noopener noreferrer">京公网安备 11000002002052号</a>
        </span>
        <span>直播间内容由AI生成</span>
      </div>
    </div>
  </footer>
</template>

<style scoped lang="scss">
.site-footer {
  position: relative;
  margin-top: 80px;
  padding-bottom: 40px;
  background: #f7f7f7;
}

/* 顶部白色信封波浪 */
.footer-wave {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 110px;
  overflow: hidden;
  line-height: 0;
}

.footer-wave svg {
  display: block;
  width: 100%;
  height: 110px;
}

/* 内容区：与设计稿 y 偏移 190px 对齐，四列顶对齐 */
.footer-body {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 1200px;
  max-width: calc(100% - 48px);
  margin: 0 auto;
  padding-top: 190px;
}

.footer-left {
  display: flex;
  align-items: flex-start;
  gap: 92px;
}

.footer-logo {
  flex: none;
  width: 104px;
  height: 113px;
  object-fit: contain;
}

.footer-column {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.footer-column-title {
  color: #171717;
  font-size: 16px;
  font-weight: 650; /* DB1 */
  line-height: 19px;
  text-decoration: none;
}

a.footer-column-title:hover {
  color: #8932ff;
}

.footer-links {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.footer-link,
.footer-contact-row {
  color: #666;
  font-size: 14px;
  font-weight: 400;
  line-height: 16px;
  text-decoration: none;
}

.footer-contact {
  width: 261px;
}

.footer-contact-row {
  display: flex;
  align-items: center;
  gap: 24px;
}

.footer-contact-row .label {
  flex: none;
  width: 28px;
}

.social-icons {
  display: flex;
  align-items: center;
  gap: 20px;
}

.social-item {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.social-icon {
  display: block;
  width: 23px;
  height: 24px;
  object-fit: contain;
}

.social-qr {
  position: absolute;
  left: 50%;
  bottom: calc(100% + 12px);
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 136px;
  height: 136px;
  padding: 8px;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  opacity: 0;
  pointer-events: none;
  transform: translateX(-50%) translateY(4px);
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.social-qr img {
  display: block;
  width: 120px;
  height: 120px;
  object-fit: contain;
}

.social-item:hover .social-qr,
.social-item:focus-visible .social-qr,
.social-item.is-active .social-qr {
  opacity: 1;
  pointer-events: auto;
  transform: translateX(-50%) translateY(0);
}

.footer-qr-wrap {
  display: flex;
  flex: none;
  flex-direction: column;
  align-items: center;
  gap: 13px;
  // cursor: pointer;
}

.footer-qr {
  display: block;
  width: 111px;
  height: 111px;
  border-radius: 14px;
  object-fit: contain;
  background: #fff;
}

.footer-qr-label {
  color: #666;
  font-size: 14px;
  font-weight: 300;
  line-height: 16px;
}

.footer-bottom {
  width: 1200px;
  max-width: calc(100% - 48px);
  margin: 64px auto 0;
}

.footer-divider {
  width: 100%;
  max-width: 1128px;
  height: 1px;
  margin: 0 auto 45px;
  background: #d8d8d8;
  opacity: 0.3;
}

.footer-copyright {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 44px;
  color: #171717;
  font-size: 14px;
  font-weight: 300; /* L */
  line-height: 16px;
  opacity: 0.4;
}

.footer-copyright .beian {
  display: inline-flex;
  gap: 0;
}

.footer-copyright .beian-icp {
  margin-right: 44px;
}

.footer-copyright a {
  color: #171717;
  text-decoration: none;
  font-weight: 400;
}

@media (max-width: 960px) {
  .site-footer {
    margin-top: 48px;
    padding-bottom: 88px; /* 给右下角悬浮球留空 */
  }

  .footer-wave {
    height: 48px;
  }

  .footer-wave svg {
    height: 48px;
  }

  .footer-body {
    box-sizing: border-box;
    width: 100%;
    max-width: none;
    flex-direction: column;
    align-items: stretch;
    gap: 28px;
    padding: 64px 20px 0;
  }

  .footer-left {
    flex-direction: column;
    align-items: stretch;
    gap: 28px;
  }

  .footer-logo {
    display: block;
    width: 72px;
    height: auto;
    margin: 0 auto;
  }

  .footer-column {
    gap: 14px;
  }

  .footer-column-title {
    font-size: 15px;
    line-height: 1.3;
  }

  /* 产品链接：两列网格，不再单列拉很长 */
  .footer-product-links {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px 16px;
  }

  .footer-link {
    line-height: 1.4;
  }

  .footer-contact {
    width: auto;
  }

  .footer-links {
    gap: 14px;
  }

  .footer-contact-row {
    align-items: flex-start;
    gap: 12px;
    line-height: 1.5;
  }

  .footer-contact-row .label {
    width: 32px;
    color: #999;
  }

  .footer-email {
    word-break: break-all;
  }

  .footer-media-row {
    align-items: center;
  }

  .social-icons {
    gap: 16px;
  }

  .social-qr {
    left: 0;
    right: auto;
    bottom: calc(100% + 10px);
    width: 120px;
    height: 120px;
    transform: translateY(4px);
  }

  .social-item:hover .social-qr,
  .social-item:focus-visible .social-qr,
  .social-item.is-active .social-qr {
    transform: translateY(0);
  }

  .social-item:last-child .social-qr {
    left: auto;
    right: 0;
    transform: translateY(4px);
  }

  .social-item:last-child:hover .social-qr,
  .social-item:last-child:focus-visible .social-qr,
  .social-item:last-child.is-active .social-qr {
    transform: translateY(0);
  }

  .social-qr img {
    width: 104px;
    height: 104px;
  }

  /* 小红书二维码居中，尺寸收敛 */
  .footer-qr-wrap {
    align-self: center;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
  }

  .footer-qr {
    width: 96px;
    height: 96px;
    border-radius: 12px;
  }

  .footer-qr-label {
    font-size: 13px;
  }

  .footer-bottom {
    box-sizing: border-box;
    width: 100%;
    max-width: none;
    margin-top: 32px;
    padding: 0 20px;
  }

  .footer-divider {
    margin: 0 auto 20px;
  }

  .footer-copyright {
    flex-direction: column;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    line-height: 1.5;
    text-align: center;
  }

  .footer-copyright .beian {
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }

  .footer-copyright .beian-icp {
    margin-right: 0;
  }
}
</style>
